import React, { useState, useEffect } from "react";

import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import { notify } from "../../../../services/toast.config";
import { toProperCase } from "../../../../helper/company_helper";

interface MarkAssetLostModalProps {
  selectedLostAsset: any;
  setSelectedLostAsset: any;
  formDataCompanyChoose: any;
  updateUI: any;
}

const MarkAssetLostModal: React.FC<MarkAssetLostModalProps> = ({
  selectedLostAsset,
  setSelectedLostAsset,
  formDataCompanyChoose,
  updateUI,
}) => {
  const signature = localStorage.getItem("signature");

  const isRecoverMode = selectedLostAsset?.status === "Lost";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");

  // Reset form whenever a new asset is selected
  useEffect(() => {
    setRemark("");
    setRemarkError("");
  }, [selectedLostAsset?.id]);

  const handleModalClose = () => {
    setRemark("");
    setRemarkError("");
    setSelectedLostAsset(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedRemark = remark.trim();

    // Remark is mandatory when marking an asset as lost
    if (!isRecoverMode && !trimmedRemark) {
      setRemarkError("Remark is required to mark an asset as lost.");
      return;
    }

    try {
      setIsSubmitting(true);

      const endpoint = isRecoverMode
        ? "assetService/recoverAsset"
        : "assetService/markAssetLost";

      const response = await api.post(endpoint, {
        signature,
        id: selectedLostAsset?.id,
        company_id: parseInt(formDataCompanyChoose?.company_id),
        company_type: formDataCompanyChoose?.company_type,
        remark: trimmedRemark,
      });

      notify.success(
        response.data.message?.[0]?.msg ||
          (isRecoverMode
            ? "Asset recovered successfully."
            : "Asset marked as lost successfully.")
      );

      // Close modal
      const dismissButton = document.querySelector(
        "#dismiss-mark-lost-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      handleModalClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        "An error occurred.";
      notify.error(errorMessage);
    } finally {
      await updateUI();
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && <Loader />}
      <div className="modal fade" id="mark_asset_lost_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <i
                  className={`ti ${
                    isRecoverMode
                      ? "ti-refresh-dot text-success"
                      : "ti-alert-triangle text-warning"
                  } me-2`}
                />
                {isRecoverMode ? "Recover Asset" : "Mark Asset as Lost"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body pb-0">
                {selectedLostAsset && (
                  <>
                    <div
                      className={`alert ${
                        isRecoverMode ? "alert-success" : "alert-warning"
                      } d-flex align-items-center`}
                    >
                      <i
                        className={`ti ${
                          isRecoverMode ? "ti-info-circle" : "ti-alert-triangle"
                        } me-2 fs-16`}
                      />
                      <span>
                        {isRecoverMode
                          ? "This asset will be recovered and set back to active for its current owner."
                          : "This asset will be marked as lost. It stays linked to its current owner for accountability."}
                      </span>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Product:</span>
                        <span className="fw-medium">
                          {toProperCase(selectedLostAsset?.product_name)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Serial Number:</span>
                        <span className="fw-medium">
                          {selectedLostAsset?.serial_no?.toUpperCase()}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-muted">
                          {isRecoverMode ? "Lost Remark:" : "Assigned To:"}
                        </span>
                        <span className="fw-medium">
                          {isRecoverMode
                            ? selectedLostAsset?.lost_remark || "N/A"
                            : toProperCase(
                                selectedLostAsset?.assigned_to_name ||
                                  "Unassigned"
                              )}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Remark{" "}
                        {isRecoverMode ? (
                          <span className="text-muted">(optional)</span>
                        ) : (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      <textarea
                        className={`form-control ${
                          remarkError ? "is-invalid border-danger" : ""
                        }`}
                        placeholder={
                          isRecoverMode
                            ? "How/where was the asset recovered..."
                            : "Explain how/where the asset was lost..."
                        }
                        rows={3}
                        maxLength={1000}
                        value={remark}
                        onChange={(e) => {
                          setRemark(e.target.value);
                          if (remarkError) setRemarkError("");
                        }}
                        disabled={isSubmitting}
                      />
                      {remarkError && (
                        <div className="text-danger mt-1">
                          <small>{remarkError}</small>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn ${
                    isRecoverMode ? "btn-success" : "btn-warning"
                  } me-2`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {isRecoverMode ? "Recovering..." : "Marking..."}
                    </>
                  ) : isRecoverMode ? (
                    "Recover Asset"
                  ) : (
                    "Mark as Lost"
                  )}
                </button>

                <button
                  id="dismiss-mark-lost-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarkAssetLostModal;
